import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, ExternalLink, Loader2, LogOut } from "lucide-react";
import { toast } from "sonner";
import {
    approveHero,
    heroDocumentUrl,
    listHeroesRequests,
    rejectHero,
    setAdminToken,
    getAdminToken,
} from "@/lib/api";

const GROUP_LABEL = {
    veteran: "Veteran",
    "active-duty": "Active-Duty Military",
    police: "Police",
    firefighter: "Firefighter",
    emt: "EMT",
    paramedic: "Paramedic",
};

export default function AdminDashboard() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionId, setActionId] = useState(null);
    const navigate = useNavigate();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await listHeroesRequests();
            setRows(data);
        } catch (err) {
            if (err?.response?.status === 401) {
                setAdminToken(null);
                navigate("/admin/login", { replace: true });
                return;
            }
            toast.error("Failed to load requests");
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        if (!getAdminToken()) {
            navigate("/admin/login", { replace: true });
            return;
        }
        load();
    }, [load, navigate]);

    async function onApprove(id) {
        setActionId(id);
        try {
            const res = await approveHero(id);
            toast.success(`Approved — coupon ${res.coupon_code} emailed`);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Approval failed");
        } finally {
            setActionId(null);
        }
    }

    async function onReject(id) {
        if (!confirm("Reject this request?")) return;
        setActionId(id);
        try {
            await rejectHero(id);
            toast.success("Rejected");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Reject failed");
        } finally {
            setActionId(null);
        }
    }

    function logout() {
        setAdminToken(null);
        navigate("/admin/login", { replace: true });
    }

    return (
        <div
            data-testid="admin-dashboard-page"
            className="mx-auto max-w-6xl px-4 py-12"
        >
            <div className="mb-8 flex items-center justify-between">
                <div>
                    <h1 className="font-display text-3xl font-extrabold text-[color:var(--brand-navy)]">
                        Heroes Discount requests
                    </h1>
                    <p className="mt-1 text-sm text-slate-600">
                        Review, approve, and issue $20 codes.
                    </p>
                </div>
                <button
                    onClick={logout}
                    data-testid="admin-logout"
                    className="pill-btn inline-flex items-center gap-2 border border-slate-200 bg-white text-sm text-[color:var(--brand-navy)]"
                >
                    <LogOut className="h-4 w-4" />
                    Sign out
                </button>
            </div>

            {loading ? (
                <div className="py-24 text-center text-slate-500">
                    <Loader2 className="mx-auto h-6 w-6 animate-spin" />
                </div>
            ) : rows.length === 0 ? (
                <div className="card-surface p-10 text-center text-slate-500">
                    No requests yet.
                </div>
            ) : (
                <div className="grid gap-4">
                    {rows.map((r) => (
                        <div
                            key={r.id}
                            data-testid={`admin-request-${r.id}`}
                            className="card-surface p-6"
                        >
                            <div className="flex flex-wrap items-start justify-between gap-4">
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h3 className="font-display text-lg font-extrabold text-[color:var(--brand-navy)]">
                                            {r.name}
                                        </h3>
                                        <span
                                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${
                                                r.status === "approved"
                                                    ? "bg-green-100 text-green-800"
                                                    : r.status === "rejected"
                                                      ? "bg-red-100 text-red-800"
                                                      : "bg-amber-100 text-amber-900"
                                            }`}
                                        >
                                            {r.status}
                                        </span>
                                    </div>
                                    <p className="mt-1 text-sm text-slate-600">
                                        {GROUP_LABEL[r.group] || r.group} ·{" "}
                                        {r.email} · {r.phone}
                                    </p>
                                    {r.message && (
                                        <p className="mt-2 text-sm text-slate-500">
                                            “{r.message}”
                                        </p>
                                    )}
                                    {r.coupon_code && (
                                        <p className="mt-2 font-mono text-sm text-[color:var(--brand-green-2)]">
                                            Code: {r.coupon_code}
                                        </p>
                                    )}
                                    <p className="mt-2 text-xs text-slate-400">
                                        Submitted{" "}
                                        {new Date(
                                            r.created_at,
                                        ).toLocaleString()}
                                    </p>
                                </div>
                                <div className="flex flex-wrap items-center gap-2">
                                    <a
                                        href={heroDocumentUrl(r.id)}
                                        target="_blank"
                                        rel="noreferrer"
                                        data-testid={`admin-doc-${r.id}`}
                                        className="pill-btn inline-flex items-center gap-2 border border-slate-200 bg-white text-sm text-[color:var(--brand-navy)]"
                                    >
                                        <ExternalLink className="h-4 w-4" />
                                        View ID
                                    </a>
                                    {r.status === "pending" && (
                                        <>
                                            <button
                                                onClick={() => onApprove(r.id)}
                                                disabled={actionId === r.id}
                                                data-testid={`admin-approve-${r.id}`}
                                                className="pill-btn inline-flex items-center gap-2 bg-[color:var(--brand-green)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                                            >
                                                <CheckCircle2 className="h-4 w-4" />
                                                Approve · Send $20
                                            </button>
                                            <button
                                                onClick={() => onReject(r.id)}
                                                disabled={actionId === r.id}
                                                data-testid={`admin-reject-${r.id}`}
                                                className="pill-btn inline-flex items-center gap-2 border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700 disabled:opacity-60"
                                            >
                                                <XCircle className="h-4 w-4" />
                                                Reject
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
